import { useEffect, useRef, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { reviewsSourceUrl } from "../data/business";

type Review = {
  author: string;
  rating: 1 | 2 | 3 | 4 | 5;
  text: string;
  date: string; // YYYY-MM or YYYY-MM-DD
  source: "google";
};

/**
 * Static fallback — real 5★ reviews pasted by Russ from the live
 * Google Business Profile (2026-05-23). These ship with the bundle so
 * the Reviews section is populated immediately, before the Vercel env
 * vars for /api/google-reviews are configured. Once the API returns
 * fresh reviews, the live data overrides this list.
 *
 * Dates here use 2025 as a conservative placeholder for "recent" —
 * Google's API will replace with real publishTime once wired up.
 */
const STATIC_FALLBACK_REVIEWS: Review[] = [
  {
    author: "Nelson Molnar",
    rating: 5,
    date: "2025-07",
    text: "What. A. Trip. From start to finish, everything was top-notch. The guides were the most experienced we've ever had: their skill, humor, and heart made the trip unforgettable. Even when one of our rafts flipped, Les jumped in without hesitation. We brought the largest group they'd ever had, 36 from Bethany Baptist Church, and they absolutely crushed it. Huge thanks to Casey, Rob, and the whole behind-the-scenes crew at ORE.",
    source: "google",
  },
  {
    author: "Thomas Morgan",
    rating: 5,
    date: "2025-07",
    text: "Our trip with Ocoee River Experience was incredible! The river rafting was thrilling, personal, and led by knowledgeable, fun guides who made us feel completely at ease. We stayed on the property, and everything from the clean cabin room to the fresh meals was perfect. It was the ideal getaway for my wife, daughter, and me. This is our new favorite spot!",
    source: "google",
  },
  {
    author: "Toni Harris",
    rating: 5,
    date: "2025-06",
    text: "This weekend we stayed in one of the newly renovated rooms at the lodge while attending a club event. Rooms were reasonably priced with everything you'd need for a weekend stay! Robert and Casey were so friendly and accommodating and we immediately felt right at home. Highly recommend the food and bar there! Breakfast and lunches were home-cooked and delicious!",
    source: "google",
  },
  {
    author: "Next Generation Martial Arts",
    rating: 5,
    date: "2025-06",
    text: "If you're planning a white water rafting trip on the Ocoee — this is the crew you want. Our group had three boats led by the most experienced guides on the river: Shoeless Joe, Les, and Dom. Each one brought unmatched skill, humor, and care. They kept us safe while giving us the wild ride we hoped for. Help this incredible family-run business continue doing what they do best.",
    source: "google",
  },
  {
    author: "Bo Mills",
    rating: 5,
    date: "2025-06",
    text: "Took a group of 25 and a fantastic time. I have been rafting numerous times on the Ocoee and this is the best trip we have ever had!! The guides and owners are very personable! Food was great as well! Les is an awesome guide. Will definitely be back!",
    source: "google",
  },
  {
    author: "Juan Torres",
    rating: 5,
    date: "2025-05",
    text: "Les and Dom were our rafting tour guides — they were absolutely amazing and the experience was amazing! The tour was 3 hours long, the cabins were clean and comfortable.",
    source: "google",
  },
  {
    author: "Mia Hahn",
    rating: 5,
    date: "2025-05",
    text: "Best rafting experience I've ever had, and I've gone rafting in Ocoee a few times already. Usually I'm quite bored, but our guides really adapted to the diverse needs of our large group of 24 people — from adventure to a chill float down the river. I highly recommend Ocoee River Experience!",
    source: "google",
  },
  {
    author: "Mike Smith",
    rating: 5,
    date: "2025-05",
    text: "Fantastic! Wonderful people with a passion for the river and of rafting it. This was only my 3rd whitewater trip through the Middle Ocoee (with 3 different outfitters) and while all 3 trips were great, rafting with Ocoee River Experience was easily the best. A slower pace was set with multiple runs through some of the obstacles. Our guide Les hit the hardest parts of the river and made them all exciting. Book your trip with them — you will not be disappointed.",
    source: "google",
  },
];

type ApiResponse = {
  reviews: Review[];
  rating: number | null;
  total: number;
  status: "ok" | "unconfigured" | "error";
};

type Props = {
  /** Optional max number of cards to render. Defaults to 6. */
  max?: number;
  /** Custom section eyebrow text. */
  eyebrow?: string;
  /** Custom section heading. */
  heading?: string;
  /** Russ feedback (2026-05-23): only show 5-star reviews on /bbq. Set
   *  `minRating={5}` to filter at the component level. Default 0 = no
   *  filter. */
  minRating?: 0 | 1 | 2 | 3 | 4 | 5;
};

/**
 * Live Google reviews with a static 5★ fallback that ships with the
 * bundle. Order of precedence:
 *   1. `/api/google-reviews` returns reviews → use those (cached 24h
 *      via Vercel Edge).
 *   2. API returns empty (env vars not yet set on Vercel, transient
 *      upstream error, or 5★-filter wipes the API list) → fall back to
 *      the STATIC_FALLBACK_REVIEWS pasted by Russ from the live profile.
 *   3. Both empty → render nothing.
 *
 * Russ refined ask (2026-05-24): "an area on the main screen where
 * different reviews would pop up, one after the other. so that you
 * would see them even if you didn't want to." → Reviews now auto-
 * rotate every ROTATE_MS (one card visible at a time), with manual
 * dot indicators and pause-on-hover. Reduced-motion users see the
 * first review only (no auto-advance).
 */
const ROTATE_MS = 7000;

export function Reviews({
  max = 6,
  eyebrow = "FROM OUR RIVERS · GOOGLE REVIEWS",
  heading = "What rafters say",
  minRating = 0,
}: Props) {
  const root = useRef<HTMLElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // Start with the static fallback so the section paints with real
  // content on first load. The API takeover happens on mount.
  const [reviews, setReviews] = useState<Review[]>(STATIC_FALLBACK_REVIEWS);
  const [aggregate, setAggregate] = useState<{ rating: number | null; total: number }>({
    rating: null,
    total: 0,
  });
  const [loaded, setLoaded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/google-reviews", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? (r.json() as Promise<ApiResponse>) : null))
      .then((data) => {
        if (cancelled || !data) return;
        // If the API came back with non-empty reviews, prefer them over
        // the static fallback. Empty → keep the fallback already in state.
        if (data.reviews && data.reviews.length > 0) {
          const sorted = [...data.reviews]
            .sort((a, b) => (a.date < b.date ? 1 : -1));
          setReviews(sorted);
        }
        if (data.rating !== null || data.total > 0) {
          setAggregate({ rating: data.rating, total: data.total });
        }
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply 5★ filter (Russ #2 — /bbq surface) then cap at max.
  const filteredReviews = reviews
    .filter((r) => (minRating > 0 ? r.rating >= minRating : true))
    .slice(0, max);

  // Auto-rotate one card at a time. Pauses on hover/focus so people
  // who want to read a long one don't lose their place. Reduced-motion
  // users get a static display of the first review.
  useEffect(() => {
    if (filteredReviews.length <= 1) return;
    if (paused) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = window.setInterval(() => {
      setActiveIdx((i) => (i + 1) % filteredReviews.length);
    }, ROTATE_MS);
    return () => window.clearInterval(t);
  }, [filteredReviews.length, paused]);

  // No GSAP fade-in: it fired on every activeIdx change INCLUDING the
  // first render, which left visitors staring at empty space for ~550ms
  // before the first review materialised ("TARDO EN APARECER"). CSS
  // handles the crossfade now — instant on initial paint, 480ms
  // crossfade between subsequent reviews (see `.reviews-rotator
  // .reviews-card` opacity rule in components.css).

  // Filter wiped out everything (e.g., 5★-only on a fresh API that
  // returned 3 reviews none of which were 5-star → falls through to
  // STATIC_FALLBACK_REVIEWS via the initial useState). If both empty,
  // render nothing — the section stays invisible until content lands.
  if (!loaded || filteredReviews.length === 0) return null;

  return (
    <section
      ref={root}
      className="reviews"
      aria-labelledby="reviews-heading"
    >
      <div className="reviews-head">
        <span className="eye eye-inverse">{eyebrow}</span>
        <h2 id="reviews-heading" className="h2 reviews-title">
          {heading}
        </h2>
        {aggregate.rating !== null && aggregate.total > 0 && (
          <p className="reviews-aggregate" aria-live="polite">
            <span className="reviews-aggregate-rating">
              {aggregate.rating.toFixed(1)}
            </span>
            <span className="reviews-aggregate-stars" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, n) => (
                <Star
                  key={n}
                  size={14}
                  className={n < Math.round(aggregate.rating ?? 0) ? "is-filled" : "is-empty"}
                />
              ))}
            </span>
            <span className="reviews-aggregate-total">
              {aggregate.total} review{aggregate.total === 1 ? "" : "s"} on Google
            </span>
          </p>
        )}
      </div>

      <div
        className="reviews-rotator"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        aria-roledescription="carousel"
        aria-live="polite"
      >
        {filteredReviews.map((r, i) => {
          const isActive = i === activeIdx;
          return (
            <article
              key={`${r.author}-${r.date}-${i}`}
              className={`reviews-card ${isActive ? "is-active" : ""}`}
              aria-hidden={!isActive}
              aria-roledescription="review"
            >
              <div className="reviews-stars" role="img" aria-label={`${r.rating} out of 5 stars`}>
                {Array.from({ length: 5 }).map((_, n) => (
                  <Star
                    key={n}
                    size={16}
                    aria-hidden="true"
                    className={n < r.rating ? "is-filled" : "is-empty"}
                  />
                ))}
              </div>
              <blockquote className="reviews-quote">
                <p>{r.text}</p>
              </blockquote>
              <footer className="reviews-byline">
                <cite className="reviews-author">{r.author}</cite>
                <span className="reviews-meta" aria-hidden="true">·</span>
                <span className="reviews-date">{formatDate(r.date)}</span>
                <span className="reviews-meta" aria-hidden="true">·</span>
                <span className="reviews-source">Google</span>
              </footer>
            </article>
          );
        })}
      </div>

      {/* Dot indicators — show position + lets the visitor jump to a
          specific review. Hidden when there's only one card. */}
      {filteredReviews.length > 1 && (
        <div className="reviews-dots" role="tablist" aria-label="Choose review">
          {filteredReviews.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === activeIdx}
              aria-label={`Show review ${i + 1} of ${filteredReviews.length}`}
              className={`reviews-dot ${i === activeIdx ? "is-active" : ""}`}
              onClick={() => setActiveIdx(i)}
            />
          ))}
        </div>
      )}

      <p className="reviews-footer">
        <a
          href={reviewsSourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="reviews-link"
        >
          Read all reviews on Google
          <ExternalLink size={14} aria-hidden="true" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </p>
    </section>
  );
}

function formatDate(d: string): string {
  const parts = d.split("-");
  if (parts.length < 2) return d;
  const year = parts[0];
  const monthIdx = parseInt(parts[1], 10) - 1;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  if (monthIdx < 0 || monthIdx > 11) return d;
  return `${months[monthIdx]} ${year}`;
}
